import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ModernTeacherSelect from './ModernTeacherSelect';
import EnhancedTeacherSelect from './EnhancedTeacherSelect';
import SectionPeriodGrid from './SectionPeriodGrid';
import DraggableGroups from './DraggableGroups';
import './ScheduleTool.css';
import './ModernTeacherSelect.css';

function getCSRFToken() {
    const cookie = document.cookie.split(';').find(item => item.trim().startsWith('csrftoken='));
    return cookie ? decodeURIComponent(cookie.split('=')[1]) : '';
}

const ScheduleTool = () => {
    const [gradeLevels, setGradeLevels] = useState([]);
    const [selectedGradeLevel, setSelectedGradeLevel] = useState(null);
    const [blockSchedules, setBlockSchedules] = useState([]);
    const [selectedBlockSchedule, setSelectedBlockSchedule] = useState(null);
    const [selectedTeachers, setSelectedTeachers] = useState([]);
    const [sectionPeriods, setSectionPeriods] = useState([]);
    const [preloadedAssociations, setPreloadedAssociations] = useState([]);
    const [proceedToScheduling, setProceedToScheduling] = useState(false);
    const [hasExistingTeachers, setHasExistingTeachers] = useState(false);
    const [droppedGroups, setDroppedGroups] = useState({});

    // Fetch grade levels
    useEffect(() => {
        axios.get('/courses/api/grade_levels/')
            .then(response => setGradeLevels(response.data))
            .catch(error => console.error('Error fetching grade levels:', error));
    }, []);

    // Fetch block schedules for the selected grade level
    useEffect(() => {
        if (selectedGradeLevel) {
            axios.get(`/schedules/api/block-schedules/?grade_level=${selectedGradeLevel}`)
                .then(response => {
                    setBlockSchedules(response.data);
                    // If there's only one block schedule, select it automatically
                    if (response.data.length === 1) {
                        setSelectedBlockSchedule(response.data[0].id);
                    }
                })
                .catch(error => console.error('Error fetching block schedules:', error));
        }
    }, [selectedGradeLevel]);
    
    // Load schedule data when block schedule is selected
    useEffect(() => {
        if (selectedBlockSchedule) {
            fetchScheduleData();
        }
    }, [selectedBlockSchedule]);

    const fetchScheduleData = async () => {
        if (!selectedGradeLevel || !selectedBlockSchedule) return;
        
        console.log("Fetching comprehensive schedule data for:", { 
            gradeLevel: selectedGradeLevel, 
            blockSchedule: selectedBlockSchedule 
        });
        
        try {
            const response = await axios.get(
                `/schedules/api/schedule-grid-data/?grade_level_id=${selectedGradeLevel}&block_schedule_id=${selectedBlockSchedule}`
            );
            
            const scheduleData = response.data;
            console.log("Schedule Grid Response:", scheduleData);
            console.log("Current assignments from API:", scheduleData.current_assignments);

            // Set section periods - sort by start time chronologically
            const sortedSectionPeriods = (scheduleData.section_periods || [])
                .sort((a, b) => a.start_time.localeCompare(b.start_time));
            setSectionPeriods(sortedSectionPeriods);

            // Extract teachers from current assignments and preloaded data
            const teachersFromAssignments = scheduleData.current_assignments || [];
            console.log("Teachers from assignments:", teachersFromAssignments);
            
            // Get unique teachers from assignments
            const uniqueTeachers = [];
            const teacherMap = new Map();
            
            teachersFromAssignments.forEach(assignment => {
                if (!teacherMap.has(assignment.teacher_id)) {
                    const teacher = { 
                        id: assignment.teacher_id, 
                        name: assignment.teacher_name,
                        last_name: assignment.teacher_name.split(' ').pop()
                    };
                    teacherMap.set(assignment.teacher_id, teacher);
                    uniqueTeachers.push(teacher);
                }
            });

            // Build initial dropped groups from current assignments
            const initialDroppedGroups = {};
            
            teachersFromAssignments.forEach(assignment => {
                if (!assignment.section_period_id || !assignment.teacher_id) return;
                
                const key = `${assignment.section_period_id}-${assignment.teacher_id}`;
                if (!initialDroppedGroups[key]) {
                    initialDroppedGroups[key] = [];
                }
                
                // Map Django model names to frontend types
                let groupType = assignment.group_type;
                if (groupType === 'homeroomgroup') groupType = 'homeroom';
                else if (groupType === 'coreclassgroup') groupType = 'core';
                else if (groupType === 'ctegroup') groupType = 'cte';
                
                initialDroppedGroups[key].push({
                    id: assignment.group_id,
                    name: assignment.group_name,
                    type: groupType,
                    sectionPeriodId: assignment.section_period_id
                });
            });

            console.log("Initial dropped groups:", initialDroppedGroups);
            
            // Update state
            setDroppedGroups(initialDroppedGroups);
            setPreloadedAssociations(teachersFromAssignments);
            
            // If we have existing teachers, use them
            if (uniqueTeachers.length > 0) {
                console.log("Teachers found from assignments:", uniqueTeachers);
                setSelectedTeachers(uniqueTeachers);
                setHasExistingTeachers(true);
                setProceedToScheduling(true);
            } else {
                console.warn("No existing teacher assignments found; requiring manual selection.");
                setHasExistingTeachers(false);
            }
        } catch (error) {
            console.error('Error fetching schedule data:', error);
            console.error('Error details:', error.response?.data);
            console.error('Error status:', error.response?.status);
            setSectionPeriods([]);
            setSelectedTeachers([]);
            setHasExistingTeachers(false);
            setProceedToScheduling(false);
        }
    };

    // Fetch comprehensive schedule data including grade configuration
    useEffect(() => {
        fetchScheduleData();
    }, [selectedGradeLevel, selectedBlockSchedule]);

    const fetchHomeroomGroups = () => {
        const teacherIds = selectedTeachers.map(teacher => teacher.id);
        axios
            .get('/schedules/api/section-periods/get_homeroom_groups/', {
                params: {
                    grade_level: selectedGradeLevel,
                    teachers: teacherIds,
                },
            })
            .then(response => {
                console.log("Fetched Homeroom Groups:", response.data);
                setDroppedGroups((prevGroups) => {
                    const updatedGroups = { ...prevGroups };
                    response.data.forEach(group => {
                        sectionPeriods.forEach(period => {
                            if (period.homeroom && group.teacher_id === period.teacher_id) {
                                const key = `${period.id}-${group.teacher_id}`;
                                updatedGroups[key] = [group];
                            }
                        });
                    });
                    return updatedGroups;
                });
            })
            .catch(error => console.error("Error fetching homeroom groups:", error));
    };

    const handleGradeLevelChange = (e) => {
        const raw = e.target.value;
        const gradeLevelId = raw === '' ? null : parseInt(raw, 10);
        setSelectedGradeLevel(gradeLevelId);
        setProceedToScheduling(false);
        setSelectedBlockSchedule(null);
        setSectionPeriods([]);
        setHasExistingTeachers(false);
        setSelectedTeachers([]);
        console.log("Grade Level Changed:", gradeLevelId);
    };

    const handleBlockScheduleChange = (event) => {
        const raw = event.target.value;
        const blockScheduleId = raw === '' ? null : parseInt(raw, 10);
        setSelectedBlockSchedule(blockScheduleId);
        setProceedToScheduling(false);
        setHasExistingTeachers(false);
        console.log("Block Schedule Changed:", blockScheduleId);
    };

    const handleTeacherSelect = (selectedTeachers) => {
        setSelectedTeachers(selectedTeachers);
        setProceedToScheduling(true);
    };

    const handleAddTeacher = () => {
        // Don't reset hasExistingTeachers, just show teacher selection
        setProceedToScheduling(false);
    };

    const handleContinue = () => {
        console.log("Proceeding to Scheduling...");
        if (selectedTeachers.length > 0) {
            setProceedToScheduling(true);
            fetchHomeroomGroups(); // Fetch and assign homeroom groups
        } else {
            console.warn("No teachers selected");
        }
    };

    const handleGroupDrop = async (group, sectionPeriodId, teacherId) => {
        const csrfToken = getCSRFToken();
        const key = sectionPeriodId ? `${sectionPeriodId}-${teacherId}` : null;
        
        // Create a backup of the current state in case we need to revert
        const previousDroppedGroups = { ...droppedGroups };
        const previousAssociations = [...preloadedAssociations];

        try {
            // Optimistically update the UI
            if (sectionPeriodId === null) {
                // Remove group assignment
                console.log("Removing group assignment:", { group, teacherId });
                
                // Find and remove the assignment
                console.log("Looking for assignment to remove:", { 
                    groupId: group.id, 
                    teacherId: teacherId,
                    groupType: group.type,
                    preloadedAssociations: preloadedAssociations 
                });
                
                const assignment = preloadedAssociations.find(a => 
                    a.group_id === group.id && 
                    a.teacher_id === teacherId &&
                    a.section_period_id === group.sectionPeriodId
                );

                console.log("Found assignment:", assignment);

                if (assignment) {
                    await axios.delete(
                        `/schedules/api/section-periods/assignments/${assignment.id}/`,
                        { headers: { "X-CSRFToken": csrfToken } }
                    );
                    
                    // Update local state after successful deletion (only from origin cell)
                    setDroppedGroups(prev => {
                        const updated = { ...prev };
                        const originKey = `${group.sectionPeriodId}-${teacherId}`;
                        updated[originKey] = (updated[originKey] || []).filter(g => 
                            !(g.id === group.id && g.type === group.type)
                        );
                        return updated;
                    });
                    
                    // Update preloaded associations
                    setPreloadedAssociations(prev => 
                        prev.filter(a => a.id !== assignment.id)
                    );
                }
            } else {
                // Assign group to section period
                console.log("Assigning group with payload:", { 
                    group_assignments: [{ type: group.type, id: group.id }], 
                    teacher_id: teacherId,
                    block_schedule_id: selectedBlockSchedule
                });

                // Optimistically update UI
                setDroppedGroups(prev => ({
                    ...prev,
                    [key]: [
                        ...(prev[key] || []).filter(g => !(g.id === group.id && g.type === group.type)),
                        { ...group, sectionPeriodId }
                    ]
                }));

                // Make the API call
                const response = await axios.post(
                    `/schedules/api/section-periods/${sectionPeriodId}/assign-groups/`,
                    { 
                        group_assignments: [{ type: group.type, id: group.id }], 
                        teacher_id: teacherId,
                        block_schedule_id: selectedBlockSchedule
                    },
                    { headers: { "X-CSRFToken": csrfToken } }
                );
                
                console.log("Group assigned:", response.data);
                
                // Update preloaded associations with the new data
                if (response.data.assignments && response.data.assignments.length > 0) {
                    setPreloadedAssociations(prev => [
                        ...prev.filter(a => 
                            !(a.group_id === group.id && 
                              a.teacher_id === teacherId &&
                              a.section_period_id === sectionPeriodId)
                        ),
                        ...response.data.assignments.map(a => ({
                            id: a.assignment_id,
                            group_id: group.id,
                            group_name: group.name,
                            group_type: group.type,
                            teacher_id: teacherId,
                            section_period_id: sectionPeriodId
                        }))
                    ]);
                }
            }
        } catch (error) {
            console.error("Error updating group assignment:", error);
            // Revert to previous state on error
            setDroppedGroups(previousDroppedGroups);
            setPreloadedAssociations(previousAssociations);
            
            // Show error to user
            alert("Failed to update schedule. Please try again.");
        }
    };

    return (
        <div className="schedule-tool-container">
            <h2>Schedule Setup</h2>
            {proceedToScheduling ? (
                <div className="scheduling-container">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h2>Schedule Setup</h2>
                        {hasExistingTeachers && (
                            <button 
                                className="btn btn-primary"
                                onClick={handleAddTeacher}
                            >
                                + Add Teacher
                            </button>
                        )}
                    </div>
                    
                    <div className="form-group">
                        <label>Grade Level</label>
                        <select 
                            className="form-control" 
                            value={selectedGradeLevel || ''} 
                            onChange={(e) => setSelectedGradeLevel(Number(e.target.value))}
                            disabled={hasExistingTeachers}
                        >
                            <option value="">Select Grade Level</option>
                            {gradeLevels.map(grade => (
                                <option key={grade.id} value={grade.id}>
                                    {grade.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Block Schedule</label>
                        <select 
                            className="form-control" 
                            value={selectedBlockSchedule || ''} 
                            onChange={(e) => setSelectedBlockSchedule(Number(e.target.value))}
                            disabled={hasExistingTeachers}
                        >
                            <option value="">Select Block Schedule</option>
                            {blockSchedules.map(schedule => (
                                <option key={schedule.id} value={schedule.id}>
                                    {schedule.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            ) : (
                <div className="form-group">
                    <label>Grade Level</label>
                    <select 
                        className="form-select"
                        value={selectedGradeLevel || ''} 
                        onChange={handleGradeLevelChange}
                    >
                        <option value="">-- Select Grade --</option>
                        {gradeLevels.map(level => (
                            <option key={level.id} value={level.id}>
                                {level.name || level.grade}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {selectedGradeLevel && !proceedToScheduling && (
                <div className="form-group">
                    <label>Block Schedule</label>
                    <select 
                        className="form-select"
                        value={selectedBlockSchedule || ''} 
                        onChange={handleBlockScheduleChange}
                    >
                        <option value="">-- Select Block --</option>
                        {blockSchedules.map(schedule => (
                            <option key={schedule.id} value={schedule.id}>
                                {schedule.name}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {selectedBlockSchedule && !proceedToScheduling && (
                <div className="teacher-selection-wrapper">
                    <EnhancedTeacherSelect 
                        selectedGradeLevel={selectedGradeLevel}
                        selectedTeachers={selectedTeachers}
                        setSelectedTeachers={setSelectedTeachers}
                        onProceedToScheduling={() => setProceedToScheduling(true)}
                    />
                </div>
            )}

            {proceedToScheduling && (
                <>
                    <div className="schedule-header">
                        <button 
                            className="add-teacher-button"
                            onClick={handleAddTeacher}
                        >
                            + Add/Modify Teachers
                        </button>
                    </div>
                    <DraggableGroups gradeLevelId={selectedGradeLevel} />
                    <SectionPeriodGrid
                        teachers={selectedTeachers}
                        sectionPeriods={sectionPeriods}
                        preloadedAssociations={preloadedAssociations}
                        droppedGroups={droppedGroups}
                        onGroupDrop={handleGroupDrop}
                    />
                </>
            )}
        </div>
    );
};

export default ScheduleTool;
